import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Api } from '../../auth/Configs';

/** Serialize saves and drain the latest draft before navigation or a review action. */
export default function useImportTaskAutosave({ id, draft, token, enabled, resetKey, getEditRevision, onSaved }) {
  const signature = JSON.stringify(draft);
  const acknowledged = useRef(signature);
  const pending = useRef(null);
  const latest = useRef(null);
  const savedRevision = useRef({ id, value: getEditRevision() });
  const mounted = useRef(true);
  const [status, setStatus] = useState('saved');
  const [error, setError] = useState('');
  const [, update] = useState(0);
  latest.current = { id, draft, signature, token, enabled, getEditRevision, onSaved };

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useLayoutEffect(() => {
    acknowledged.current = latest.current.signature;
    savedRevision.current = { id: latest.current.id, value: latest.current.getEditRevision() };
    setStatus('saved'); setError('');
    update((value) => value + 1);
  }, [resetKey, id]);

  const flush = useCallback(async () => {
    const run = async () => {
      while (latest.current.id && latest.current.enabled && latest.current.signature !== acknowledged.current) {
        const snapshot = latest.current;
        if (mounted.current) { setStatus('saving'); setError(''); }
        try {
          const { data } = await axios.patch(`${Api}/ged/documents/import-zip/tasks/${snapshot.id}`, {
            editRevision: Math.max(snapshot.getEditRevision(), savedRevision.current.id === snapshot.id ? savedRevision.current.value : 0), draft: snapshot.draft,
          }, { headers: { Authorization: `Bearer ${snapshot.token}` } });
          if (latest.current.id !== snapshot.id) return;
          acknowledged.current = snapshot.signature;
          savedRevision.current = { id: snapshot.id, value: data.editRevision };
          if (mounted.current) snapshot.onSaved(data);
          if (mounted.current) { setStatus('saved'); update((value) => value + 1); }
        } catch (err) {
          if (mounted.current) { setStatus('error'); setError(err.response?.data?.message || 'La sauvegarde a échoué. Votre saisie reste affichée ; réessayez avant de quitter.'); }
          throw err;
        }
      }
    };
    const request = (pending.current ? pending.current.catch(() => {}) : Promise.resolve()).then(run);
    pending.current = request;
    try { await request; } finally { if (pending.current === request) pending.current = null; }
  }, []);

  const dirty = Boolean(id) && signature !== acknowledged.current;
  useEffect(() => {
    if (!dirty || !enabled || status === 'error') return undefined;
    const timer = setTimeout(() => { flush().catch(() => {}); }, 1000);
    return () => clearTimeout(timer);
  }, [dirty, enabled, signature, flush, status]);

  useEffect(() => {
    const protect = (event) => {
      if (!latest.current.id) return;
      if (latest.current.signature === acknowledged.current && !pending.current) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, []);

  useEffect(() => () => { flush().catch(() => {}); }, [flush]);

  return { flush, dirty, saving: status === 'saving', error, status: dirty && status === 'saved' ? 'dirty' : status };
}
